import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Menu,
  X,
  ArrowRight,
  ArrowUp,
  Sparkles,
  Megaphone,
  ShieldAlert,
  FlaskConical,
  ArrowUpRight,
  Globe,
  ChevronDown,
} from 'lucide-react';
import './LandingPage.css';
import logo from '../assets/images/ReactFirstR.png';
import heroMap from '../assets/images/narrative-map.webp';

/* ----------------------------- static config ----------------------------- */
const NAV_KEYS = [
  { key: 'platform', href: '#platform' },
  { key: 'how', href: '#how-it-works' },
  { key: 'useCases', href: '#use-cases' },
];

const CALLOUT_POS = [
  { left: '12%', top: '46%' },
  { left: '40%', top: '38%' },
  { right: '26%', top: '44%' },
  { right: '12%', bottom: '24%' },
];

const USE_ICONS = [Megaphone, ShieldAlert, FlaskConical];

const LANGS = [
  { code: 'en', label: 'English' },
  { code: 'he', label: 'עברית' },
  { code: 'fr', label: 'Français' },
];

/* ----------------------------- helpers ----------------------------- */
const asArray = (val) => (Array.isArray(val) ? val : []);

function useTypewriter(phrases) {
  const [text, setText] = useState('');
  const [index, setIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!phrases.length) return undefined;
    const current = phrases[index % phrases.length];
    let delay = deleting ? 35 : 55;

    if (!deleting && text === current) {
      delay = 2200;
    } else if (deleting && text === '') {
      setDeleting(false);
      setIndex((i) => i + 1);
      delay = 350;
    }

    const t = setTimeout(() => {
      if (!deleting && text === current) {
        setDeleting(true);
        return;
      }
      const next = deleting ? current.slice(0, text.length - 1) : current.slice(0, text.length + 1);
      setText(next);
    }, delay);

    return () => clearTimeout(t);
  }, [text, deleting, index, phrases]);

  return text;
}

const reveal = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
};

/* ----------------------------- language switcher ----------------------------- */
function LangSwitcher() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const current = LANGS.find((l) => i18n.language?.startsWith(l.code)) || LANGS[0];

  return (
    <div className="rf-lang" ref={ref}>
      <button type="button" className="rf-lang__btn" onClick={() => setOpen((v) => !v)} aria-label="Language">
        <Globe size={16} />
        <span>{current.code.toUpperCase()}</span>
        <ChevronDown size={14} className={`rf-lang__chev ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="rf-lang__menu">
          {LANGS.map((l) => (
            <button
              key={l.code}
              type="button"
              className={`rf-lang__item ${current.code === l.code ? 'active' : ''}`}
              onClick={() => {
                i18n.changeLanguage(l.code);
                setOpen(false);
              }}
            >
              {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ----------------------------- sections ----------------------------- */
function SiteNav({ onSignIn, onDemo, scrolled }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <motion.header
      initial={{ y: -24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={`rf-nav ${scrolled || open ? 'rf-nav--scrolled' : ''}`}
    >
      <nav className="rf-nav__inner">
        <button type="button" className="rf-logo" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <img src={logo} alt="ReactFirst" />
          <span className="rf-logo__text">
            ReactFirst<span className="rf-logo__ai">AI</span>
          </span>
        </button>

        <div className="rf-nav__links">
          {NAV_KEYS.map((link) => (
            <a key={link.key} href={link.href} className="rf-nav__link">
              {t(`lp.nav.${link.key}`)}
            </a>
          ))}
        </div>

        <div className="rf-nav__actions">
          <LangSwitcher />
          <button type="button" className="rf-btn rf-btn--ghost rf-btn--sm" onClick={onSignIn}>
            {t('lp.nav.signIn')}
          </button>
          <button type="button" className="rf-btn rf-btn--primary rf-btn--sm" onClick={onDemo}>
            {t('lp.nav.demo')}
          </button>
        </div>

        <button type="button" aria-label="Toggle menu" className="rf-nav__burger" onClick={() => setOpen((v) => !v)}>
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="rf-nav__mobile">
          {NAV_KEYS.map((link) => (
            <a key={link.key} href={link.href} className="rf-nav__link" onClick={() => setOpen(false)}>
              {t(`lp.nav.${link.key}`)}
            </a>
          ))}
          <div className="rf-nav__mobile-lang">
            <LangSwitcher />
          </div>
          <button type="button" className="rf-btn rf-btn--secondary rf-btn--lg" onClick={() => { setOpen(false); onSignIn(); }}>
            {t('lp.nav.signIn')}
          </button>
          <button type="button" className="rf-btn rf-btn--primary rf-btn--lg" onClick={() => { setOpen(false); onDemo(); }}>
            {t('lp.nav.demo')}
          </button>
        </div>
      )}
    </motion.header>
  );
}

function PromptBar() {
  const { t } = useTranslation();
  const phrases = asArray(t('lp.hero.prompts', { returnObjects: true }));
  const typed = useTypewriter(phrases);
  return (
    <div className="rf-prompt">
      <div className="rf-prompt__row">
        <Sparkles size={16} className="rf-prompt__spark" />
        <span className="rf-prompt__text">
          {typed}
          <span className="rf-cursor" />
        </span>
        <button type="button" className="rf-prompt__send" aria-label="Send">
          <ArrowUp size={16} strokeWidth={2.25} />
        </button>
      </div>
    </div>
  );
}

function Hero({ onDemo }) {
  const { t, i18n } = useTranslation();
  const callouts = asArray(t('lp.callouts', { returnObjects: true }));
  return (
    <section className="rf-hero">
      <div className="rf-hero__map">
        <img src={heroMap} alt={t('lp.hero.caption')} />
        <div className="rf-hero__map-fade" />
        {callouts.map((s, i) => (
          <motion.div
            key={s.label}
            className="rf-callout"
            style={CALLOUT_POS[i]}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 + i * 0.12 }}
          >
            <span className="rf-callout__label">
              <span className="rf-callout__dot" />
              {s.label}
            </span>
            <span className="rf-callout__value">{s.value}</span>
          </motion.div>
        ))}
      </div>

      <div className="rf-glow rf-hero__glow" aria-hidden="true" />

      <div className="rf-hero__inner">
        <motion.a
          href="#how-it-works"
          className="rf-pill"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        >
          <span className="rf-ping">
            <span />
            <span />
          </span>
          {t('lp.hero.badge')}
          <ArrowRight className="rf-btn__arrow" size={14} />
        </motion.a>

        <motion.h1
          className="rf-hero__title"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <span className="rf-italic">{t('lp.hero.title1')}</span>
          <br />
          <span className="rf-grad">{t('lp.hero.title2')}</span>
        </motion.h1>

        <motion.p
          className="rf-hero__sub"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
        >
          {t('lp.hero.sub')}
        </motion.p>

        <motion.div
          className="rf-hero__ctas"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: 'easeOut' }}
        >
          <button type="button" className="rf-btn rf-btn--primary rf-btn--lg" onClick={onDemo}>
            {t('lp.hero.demo')}
            <ArrowRight className="rf-btn__arrow" size={16} />
          </button>
          <a href="#how-it-works" className="rf-btn rf-btn--secondary rf-btn--lg">
            {t('lp.hero.how')}
          </a>
        </motion.div>

        <motion.div
          className="rf-prompt-wrap"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.32, ease: 'easeOut' }}
        >
          <PromptBar key={i18n.language} />
        </motion.div>
      </div>

      <div className="rf-hero__caption">
        <span className="rf-hero__caption-dot" />
        <span>{t('lp.hero.caption')}</span>
      </div>
    </section>
  );
}

function PlatformOverview() {
  const { t } = useTranslation();
  const steps = asArray(t('lp.platform.steps', { returnObjects: true }));
  const orbit = asArray(t('lp.platform.orbit', { returnObjects: true }));
  return (
    <section id="platform" className="rf-platform">
      <div className="rf-container">
        <div className="rf-platform__grid">
          <div>
            <p className="rf-eyebrow rf-platform__eyebrow">{t('lp.platform.eyebrow')}</p>
            <h2 className="rf-h2">{t('lp.platform.heading')}</h2>
            <ol className="rf-timeline">
              {steps.map((step, i) => (
                <motion.li
                  key={step.title}
                  initial={{ opacity: 0, x: -12 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                >
                  <span className="rf-timeline__dot" />
                  <h3>{step.title}</h3>
                  <p>{step.body}</p>
                </motion.li>
              ))}
            </ol>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <motion.div
              className="rf-orbit"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
            >
              <svg viewBox="0 0 400 400" aria-hidden="true">
                <defs>
                  <radialGradient id="rfCoreGlow" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor="#a892ff" stopOpacity="0.55" />
                    <stop offset="60%" stopColor="#a892ff" stopOpacity="0.12" />
                    <stop offset="100%" stopColor="#a892ff" stopOpacity="0" />
                  </radialGradient>
                </defs>
                <g className="rf-orbit__spin">
                  <ellipse cx="200" cy="200" rx="150" ry="150" fill="none" stroke="#cfcabb" strokeWidth="1" />
                  <ellipse cx="200" cy="200" rx="150" ry="90" fill="none" stroke="#cfcabb" strokeWidth="1" />
                  <ellipse cx="200" cy="200" rx="90" ry="150" fill="none" stroke="#cfcabb" strokeWidth="1" />
                  <circle cx="200" cy="50" r="4" fill="#101217" />
                  <circle cx="350" cy="200" r="4" fill="#101217" />
                  <circle cx="200" cy="350" r="4" fill="#a892ff" />
                  <circle cx="50" cy="200" r="4" fill="#101217" />
                </g>
                <circle className="rf-orbit__core" cx="200" cy="200" r="74" fill="url(#rfCoreGlow)" />
                <circle cx="200" cy="200" r="30" fill="#15171d" />
                <circle cx="188" cy="194" r="5" fill="#a892ff" />
                <circle cx="210" cy="206" r="5" fill="#a892ff" />
                <circle cx="206" cy="188" r="4" fill="#b9d86a" />
                <line x1="188" y1="194" x2="210" y2="206" stroke="#a892ff" strokeWidth="1.2" />
                <line x1="206" y1="188" x2="188" y2="194" stroke="#a892ff" strokeWidth="1.2" />
              </svg>
              <span className="rf-orbit__label top">{orbit[0]}</span>
              <span className="rf-orbit__label right">{orbit[1]}</span>
              <span className="rf-orbit__label bottom">{orbit[2]}</span>
              <span className="rf-orbit__label left">{orbit[3]}</span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Metrics() {
  const { t } = useTranslation();
  const items = asArray(t('lp.metrics.items', { returnObjects: true }));
  return (
    <section className="rf-metrics">
      <div className="rf-dotgrid" aria-hidden="true" />
      <div className="rf-glow rf-metrics__glow" aria-hidden="true" />
      <div className="rf-container">
        <div className="rf-metrics__head">
          <motion.p className="rf-eyebrow" {...reveal} transition={{ duration: 0.5 }}>
            {t('lp.metrics.eyebrow')}
          </motion.p>
          <motion.h2 className="rf-h2" {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>
            {t('lp.metrics.heading')}
          </motion.h2>
        </div>

        <div className="rf-bento">
          {items.map((m, i) => (
            <motion.div
              key={i}
              className="rf-bento__cell"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
            >
              <span className="rf-bento__num">{m.value}</span>
              <p className="rf-bento__label">{m.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const { t } = useTranslation();
  const stages = asArray(t('lp.how.steps', { returnObjects: true }));
  return (
    <section id="how-it-works" className="rf-how">
      <div className="rf-glow rf-how__glow" aria-hidden="true" />
      <div className="rf-container rf-how__inner">
        <div className="rf-how__head">
          <motion.p className="rf-eyebrow" {...reveal} transition={{ duration: 0.5 }}>
            {t('lp.how.eyebrow')}
          </motion.p>
          <motion.h2 className="rf-h2" {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>
            {t('lp.how.heading')}
          </motion.h2>
        </div>

        <div className="rf-steps">
          {stages.map((stage, i) => {
            const isLast = i === stages.length - 1;
            return (
              <motion.div
                key={stage.step}
                className="rf-step"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                <div className="rf-step__rail">
                  <span className={`rf-step__node ${isLast ? 'lime' : ''}`} />
                  <span className="rf-step__line" />
                </div>
                <span className="rf-step__num">{stage.step}</span>
                <h3>{stage.title}</h3>
                <p>{stage.body}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const { t } = useTranslation();
  const items = asArray(t('lp.uses.items', { returnObjects: true }));
  return (
    <section id="use-cases" className="rf-uses">
      <div className="rf-glow rf-uses__glow" aria-hidden="true" />
      <div className="rf-container rf-uses__inner">
        <motion.p className="rf-eyebrow" {...reveal} transition={{ duration: 0.5 }}>
          {t('lp.uses.eyebrow')}
        </motion.p>
        <motion.h2 className="rf-h2" {...reveal} transition={{ duration: 0.5, delay: 0.05 }}>
          {t('lp.uses.heading')}
        </motion.h2>

        <div className="rf-rows">
          {items.map((useCase, i) => {
            const Icon = USE_ICONS[i] || Megaphone;
            return (
              <motion.div
                key={useCase.title}
                className="rf-row"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
              >
                <span className="rf-row__rail" />
                <span className="rf-row__icon">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <h3 className="rf-row__title">{useCase.title}</h3>
                <p className="rf-row__body">{useCase.body}</p>
                <ArrowUpRight className="rf-row__arrow" size={20} />
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ClosingCta({ onDemo }) {
  const { t } = useTranslation();
  return (
    <section id="demo" className="rf-cta">
      <div className="rf-glow rf-cta__glow" aria-hidden="true" />
      <svg className="rf-cta__route" viewBox="0 0 1200 160" preserveAspectRatio="none" aria-hidden="true">
        <motion.path
          d="M0 120 C 220 60, 360 140, 560 90 S 920 40, 1200 100"
          fill="none"
          stroke="#b9d86a"
          strokeWidth="1.5"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: 'easeInOut' }}
        />
        <motion.path
          d="M0 140 C 260 90, 420 150, 640 120 S 980 80, 1200 130"
          fill="none"
          stroke="#a892ff"
          strokeWidth="1"
          strokeOpacity="0.7"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, delay: 0.2, ease: 'easeInOut' }}
        />
        <circle cx="560" cy="90" r="4" fill="#b9d86a" />
        <circle cx="220" cy="86" r="3.5" fill="#a892ff" />
        <circle cx="920" cy="64" r="3.5" fill="#a892ff" />
      </svg>

      <motion.div
        className="rf-cta__inner"
        initial={{ opacity: 0, y: 18 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-80px' }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="rf-h2">{t('lp.cta.heading')}</h2>
        <p className="rf-cta__sub">{t('lp.cta.sub')}</p>
        <div className="rf-cta__btn">
          <button type="button" className="rf-btn rf-btn--primary rf-btn--lg" onClick={onDemo}>
            {t('lp.cta.demo')}
            <ArrowRight className="rf-btn__arrow" size={16} />
          </button>
        </div>
      </motion.div>
    </section>
  );
}

function Footer({ onNav, onSignIn }) {
  const { t } = useTranslation();
  return (
    <footer className="rf-footer">
      <div className="rf-footer__inner">
        <div className="rf-footer__top">
          <div className="rf-footer__brand">
            <div className="rf-footer__lock">
              <img src={logo} alt="ReactFirst" />
              <span className="rf-logo__text">
                ReactFirst<span className="rf-logo__ai">AI</span>
              </span>
            </div>
            <p className="rf-footer__tag">{t('lp.footer.tagline')}</p>
          </div>

          <div className="rf-footer__cols">
            <div className="rf-footer__col">
              <h3>{t('lp.footer.platform')}</h3>
              <ul>
                <li><a href="#how-it-works">{t('lp.footer.how')}</a></li>
                <li><a href="#use-cases">{t('lp.footer.useCases')}</a></li>
                <li><a href="#demo">{t('lp.nav.demo')}</a></li>
              </ul>
            </div>
            <div className="rf-footer__col">
              <h3>{t('lp.footer.company')}</h3>
              <ul>
                <li><button type="button" onClick={() => onNav('/privacy')}>{t('lp.footer.privacy')}</button></li>
                <li><button type="button" onClick={() => onNav('/terms')}>{t('lp.footer.terms')}</button></li>
                <li><button type="button" onClick={onSignIn}>{t('lp.footer.signIn')}</button></li>
              </ul>
            </div>
          </div>
        </div>

        <div className="rf-footer__bottom">
          <p>© {new Date().getFullYear()} ReactFirst AI. {t('lp.footer.rights')}</p>
        </div>
      </div>
    </footer>
  );
}

/* ----------------------------- page ----------------------------- */
const LandingPage = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const goLogin = () => navigate('/login');
  const sentinelRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const isRTL = (i18n.language || '').startsWith('he');

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => setScrolled(!entry.isIntersecting),
      { threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // SEO: canonical + hreflang for the marketing landing only (removed on unmount)
  useEffect(() => {
    const ORIGIN = 'https://dashboard.reactfirst.ai';
    const added = [];
    const add = (rel, href, hreflang) => {
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (hreflang) link.hreflang = hreflang;
      link.dataset.lpSeo = 'true';
      document.head.appendChild(link);
      added.push(link);
    };
    add('canonical', `${ORIGIN}/`);
    add('alternate', `${ORIGIN}/?lng=en`, 'en');
    add('alternate', `${ORIGIN}/?lng=he`, 'he');
    add('alternate', `${ORIGIN}/?lng=fr`, 'fr');
    add('alternate', `${ORIGIN}/`, 'x-default');
    return () => added.forEach((l) => l.remove());
  }, []);

  return (
    <div className="rf-landing" dir={isRTL ? 'rtl' : 'ltr'}>
      <span ref={sentinelRef} aria-hidden="true" style={{ position: 'absolute', top: 0, insetInlineStart: 0, height: 1, width: 1 }} />
      <SiteNav onSignIn={goLogin} onDemo={goLogin} scrolled={scrolled} />
      <Hero onDemo={goLogin} />
      <PlatformOverview />
      <Metrics />
      <HowItWorks />
      <UseCases />
      <ClosingCta onDemo={goLogin} />
      <Footer onNav={navigate} onSignIn={goLogin} />
    </div>
  );
};

export default LandingPage;
